import { Scale, Settings, Users, Wallet } from 'lucide-react';
import { createElement } from 'react';

import { tatCaTabCaiDat } from '../../lib/caiDatTabs.js';
import { ChuyenHuong } from '../../lib/guard.js';
import type { KhaiBaoModule } from '../../lib/modules.js';
import { TrangChapNhanLoiMoi } from './pages/TrangChapNhanLoiMoi.js';
import { TrangBienBan } from './pages/TrangBienBan.js';
import { TrangCaiDat } from './pages/TrangCaiDat.js';
import { TrangDoiSoatNhanSu } from './pages/TrangDoiSoatNhanSu.js';
import { TrangThuNhapCuaToi } from './pages/TrangThuNhapCuaToi.js';
import { TrangHoSoLuuTru } from './pages/TrangHoSoLuuTru.js';
import { TrangNhanSuVaNhom } from './pages/TrangNhanSuVaNhom.js';

const team: KhaiBaoModule = {
  id: 'team',
  nav: {
    group: 'QUẢN TRỊ',
    label: 'Nhân sự & nhóm',
    icon: Users,
    order: 1,
    permission: 'team',
    to: '/nhan-su',
  },
  /**
   * Mục menu thứ hai trở đi của module. `nav` chỉ nhận một mục nên khai ở đây.
   *
   * Bước 3/3 đã **gộp** "Nhóm & đội ngũ" và "Hồ sơ thuế nhân sự" thành tab của
   * `/nhan-su`; hai đường dẫn cũ chuyển hướng sang `?tab=` tương ứng.
   */
  navThem: [
    {
      group: 'TÀI CHÍNH',
      label: 'Đối soát',
      icon: Scale,
      order: 2,
      permission: 'team',
      mucQuyen: 'admin',
      badge: 'doi-soat-nhap',
      to: '/doi-soat-nhan-su',
    },
    {
      /*
       * "Thu nhập của tôi" hiện với **mọi** người đăng nhập: đây là tiền của
       * chính họ, mà cộng tác viên thường có `team: none`. Route cũng chỉ đòi
       * đăng nhập — server lọc theo `req.currentUser.id`.
       *
       * ⛔ `permission: 'core'` **không đủ** để diễn đạt điều đó: ma trận mặc
       * định đặt `core = none` cho cả bốn vai trò không phải quản trị, nên mục
       * này bị ẩn với đúng những người nó dành cho (18/10/2026). Ô `permission`
       * vẫn phải khai vì nó là bắt buộc trong `KhaiBaoNav`; `luonHien` mới là
       * luật thật.
       */
      group: 'TÀI CHÍNH',
      label: 'Thu nhập của tôi',
      icon: Wallet,
      order: 3,
      permission: 'core',
      luonHien: true,
      to: '/thu-nhap-cua-toi',
    },
    {
      /*
       * "Cài đặt" là **khung chứa** mục cấu hình của mọi module (`/cai-dat`):
       * Công ty (module 10) và Kết nối API (module 03). Route của khung không
       * khai quyền; mỗi mục tự đòi quyền của module nó thuộc về.
       *
       * ⛔ Vì vậy đòi `team:admin` ở mục menu là sai: người có `sync:edit`
       * (vai trò Quản lý kênh) không thấy mục nào, trong khi khung trống của
       * trang Kênh lại chỉ họ "vào Cài đặt → Kết nối API" (18/10/2026).
       * `quyenBatKy` hỏi đúng danh sách mục đã đăng ký, nên module nào thêm
       * một mục cấu hình là menu tự mở cho người có quyền của module đó.
       */
      group: 'QUẢN TRỊ',
      label: 'Cài đặt',
      icon: Settings,
      order: 3,
      permission: 'team',
      mucQuyen: 'admin',
      quyenBatKy: () => tatCaTabCaiDat().map((t) => t.permission),
      to: '/cai-dat',
    },
  ],
  routes: [
    /* ------------------- Ba đường dẫn cũ, giữ để link cũ còn mở ------------------- */
    {
      path: '/nhom-doi-ngu',
      element: createElement(ChuyenHuong, { den: '/nhan-su?tab=nhom' }),
      permission: { module: 'team', level: 'view' },
    },
    {
      path: '/nhan-su/ho-so-thue',
      element: createElement(ChuyenHuong, { den: '/nhan-su?tab=ho-so-thue' }),
      permission: { module: 'team', level: 'admin' },
    },
    {
      path: '/cai-dat-cong-ty',
      element: createElement(ChuyenHuong, { den: '/cai-dat?tab=cong-ty' }),
      permission: { module: 'team', level: 'admin' },
    },
    {
      /*
       * Chỉ quản trị nhân sự: trang bày tiền của mọi người cạnh nhau. Trang để
       * từng người tự xem phần của mình là việc của bước 3.
       */
      path: '/doi-soat-nhan-su',
      element: createElement(TrangDoiSoatNhanSu),
      permission: { module: 'team', level: 'admin' },
    },
    {
      /*
       * Khung Cài đặt: **không** khai quyền ở route. Mỗi mục tự đòi quyền của
       * module nó thuộc về (xem `lib/caiDatTabs`), nên người chỉ có `sync:view`
       * vẫn mở được trang và chỉ thấy mục "Kết nối API".
       */
      path: '/cai-dat',
      element: createElement(TrangCaiDat),
    },
    {
      /*
       * Chỉ cần **đăng nhập**: trang chỉ trả dữ liệu của chính người đang xem
       * (server lọc theo `req.currentUser.id`), và người nhận tiền có thể không
       * có quyền `team` nào.
       */
      path: '/thu-nhap-cua-toi',
      element: createElement(TrangThuNhapCuaToi),
    },
    {
      /* Biên bản: quản trị, hoặc chính người nhận — server kiểm. */
      path: '/bien-ban/:id',
      element: createElement(TrangBienBan),
    },
    {
      /* Ba tab: Thành viên · Nhóm · Hồ sơ thuế (tab cuối chỉ `team:admin`). */
      path: '/nhan-su',
      element: createElement(TrangNhanSuVaNhom),
      permission: { module: 'team', level: 'view' },
    },
    {
      // Hồ sơ chỉ xem của nhân sự đã nghỉ. `team:view` là đủ — đây là dữ liệu
      // lịch sử, không sửa được gì; nút Khôi phục trong trang tự ẩn với người
      // không phải quản trị và API vẫn đòi `team:admin`.
      path: '/nhan-su/luu-tru/:id',
      element: createElement(TrangHoSoLuuTru),
      permission: { module: 'team', level: 'view' },
    },
    {
      // Công khai: người được mời chưa có mật khẩu nên chưa đăng nhập được.
      path: '/moi/:token',
      element: createElement(TrangChapNhanLoiMoi),
      congKhai: true,
    },
  ],
};

export default team;
